"use client";

import { Close } from "@mui/icons-material";
import { ReactNode, MouseEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { Box } from "../Box/Box";
import { Button } from "../Button/Button";
import { EmbeddedToolbar } from "../EmbeddedToolbar/EmbeddedToolbar";
import { SafeAreaBottom } from "../SafeArea/SafeArea";
import { Text } from "../Text/Text";
import styles from "./Modal.module.css";
import { useEmbedded } from "~/hooks/embedded";

interface Props {
  busy?: boolean; // busy will block the ability to close the modal
  children: ReactNode;
  footer?: ReactNode;
  hideHeader?: boolean; // remove header from the dom
  open: boolean;
  title?: string;
  width?: "standard" | "large";
  removeGutter?: boolean;
  onClose(): void;
  embedded?: boolean;
  embeddedDesign?: "original" | "primary" | "white" | "black" | "transparent";
  background?: "jets-beige";
}

export function ModalNew(props: Props): React.ReactElement | null {
  const {
    busy,
    children,
    footer,
    hideHeader,
    open,
    title,
    width,
    onClose,
    embedded,
  } = props;

  if (!props.open) return null;

  const modalClassNames = [styles.modalNamespace, styles.modalContainer];

  if (width === "large") {
    modalClassNames.push(styles.wideModal);
  }

  const handleClose = () => {
    if (busy) {
      return;
    }
    onClose();
  };

  const preventClose = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Box>
      <FocusLock group="modal" disabled={!open}>
        <Box
          className={styles.modalBackground}
          onClick={handleClose}
          display="flex"
          position="fixed"
          width="full"
          height="full"
          alignItems="center"
          justifyContent="center"
          userSelect="none"
          flexDirection="column"
        >
          <Box
            role="dialog"
            aria-modal="true"
            aria-label={title}
            background={props.background || "white"}
            className={modalClassNames}
            onClick={preventClose}
            width="full"
            height="full"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            {!hideHeader && !embedded && (
              <ModalHeaderNew
                onClose={handleClose}
                title={title}
                busy={props.busy || false}
              />
            )}
            {embedded && (
              <EmbeddedToolbar
                title={props.title}
                onBack={handleClose}
                design={props.embeddedDesign}
              />
            )}
            <ModalContent open={open} removeGutter={props.removeGutter}>
              {children}
            </ModalContent>
            {footer && <ModalFooter>{footer}</ModalFooter>}
          </Box>
        </Box>
      </FocusLock>
    </Box>
  );
}

export function Modal(props: Props): React.ReactElement | null {
  const { busy, children, footer, hideHeader, open, title, width, onClose } =
    props;

  const [backgroundElement, setBackgroundElement] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (backgroundElement) return;

    // only create the element on client side by using useEffect
    const element = document.createElement("div");
    element.style.display = "none";
    element.tabIndex = -1;

    // this appends a div every time the modal opens
    // isn't that kind of against what portal does?
    document.body.appendChild(element);

    setBackgroundElement(element);
  }, [backgroundElement]);

  const preventClose = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleClose = () => {
    if (busy) {
      return;
    }
    onClose();
  };

  const modalClassNames = [styles.modalNamespace, styles.modalContainer];

  if (width === "large") {
    modalClassNames.push(styles.wideModal);
  }

  if (!backgroundElement) return null;

  // this is weird
  if (open) {
    backgroundElement.style.display = "flex";
  } else {
    backgroundElement.style.display = "none";
  }
  backgroundElement.tabIndex = -1;

  return createPortal(
    <Box>
      <FocusLock group="modal" disabled={!open}>
        <Box
          className={styles.modalBackground}
          onClick={handleClose}
          display="flex"
          position="fixed"
          width="full"
          height="full"
          alignItems="center"
          justifyContent="center"
          userSelect="none"
          flexDirection="column"
        >
          <Box
            role="dialog"
            aria-modal="true"
            aria-label={title}
            background="white"
            className={modalClassNames}
            onClick={preventClose}
            width="full"
            height="full"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            {!hideHeader && <ModalHeader onClose={handleClose} title={title} />}
            <ModalContent open={open} removeGutter={props.removeGutter}>
              {children}
            </ModalContent>
            {footer && <ModalFooter>{footer}</ModalFooter>}
          </Box>
        </Box>
      </FocusLock>
    </Box>,
    backgroundElement,
  );
}

function ModalHeader(props: { onClose: () => void; title?: string }) {
  const { isEmbedded } = useEmbedded();
  const safeAreaInset =
    typeof window !== "undefined" && window.ReactNativeWebView?.safeAreaInset;

  return (
    <Box position="relative" padding="5" background="primary">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        className={isEmbedded ? styles.embeddedHeader : undefined}
        style={safeAreaInset ? { paddingTop: `${safeAreaInset.top}px` } : {}}
      >
        {props.title && (
          <Text size="large" color="overPrimary">
            {props.title}
          </Text>
        )}
      </Box>
      <Box position="absolute" style={{ top: 0, right: 0, bottom: 0 }}>
        <Box
          display="flex"
          height="full"
          alignItems="center"
          paddingRight="4"
          className={isEmbedded ? styles.embeddedHeader : undefined}
          style={safeAreaInset ? { paddingTop: `${safeAreaInset.top}px` } : {}}
        >
          <Button
            onClick={props.onClose}
            short
            variant="borderless"
            ariaLabel="Close modal"
          >
            <Close htmlColor={"var(--color-over-primary)"} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

interface ModalHeaderNewProps {
  onClose: () => void;
  title?: string;
  busy: boolean;
}

export function ModalHeaderNew({ onClose, title, busy }: ModalHeaderNewProps) {
  const { isEmbedded } = useEmbedded();
  const [isFocused, setIsFocused] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const safeAreaInset =
    typeof window !== "undefined" && window.ReactNativeWebView?.safeAreaInset;

  useEffect(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.blur();
    }
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!busy) {
        onClose();
      }
    }
  };

  return (
    <Box position="relative" padding="4" background="primary">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        className={isEmbedded ? styles.embeddedHeader : undefined}
        style={safeAreaInset ? { paddingTop: `${safeAreaInset.top}px` } : {}}
      >
        <Text
          size="large"
          color="overPrimary"
          font="special"
          weight="strong"
          transform="uppercase"
        >
          {title || "\u00A0"}
        </Text>
      </Box>
      <Box position="absolute" style={{ top: 0, right: 0, bottom: 0 }}>
        <Box
          as="button"
          ref={closeButtonRef}
          display="flex"
          height="full"
          alignItems="center"
          paddingLeft="4"
          paddingRight="4"
          background="primary"
          onClick={busy ? undefined : onClose}
          onKeyDown={handleKeyDown}
          className={`${isEmbedded ? styles.embeddedHeader : ""}`}
          border="standard"
          borderColor={isFocused ? "primary-highlight" : "accent"}
          tabIndex={0}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            cursor: busy ? "default" : "pointer",
            paddingTop: safeAreaInset ? safeAreaInset.top : undefined,
            outline: "none",
          }}
          aria-label="Close modal"
        >
          <Close
            htmlColor={"var(--color-over-primary)"}
            style={{ fontWeight: 900, fontSize: 32 }}
          />
        </Box>
      </Box>
    </Box>
  );
}

function ModalContent(props: {
  open: boolean;
  children: ReactNode;
  removeGutter?: boolean;
}) {
  const modalRef = useRef<HTMLElement>(null);
  return (
    <RemoveScroll ref={modalRef} forwardProps enabled={props.open}>
      <Box
        padding={props.removeGutter ? undefined : ["3", "5"]}
        // className={styles.modalContent}
        style={{
          overflowY: "auto",
          flex: 1,
        }}
      >
        <Box height="full">{props.children}</Box>
      </Box>
    </RemoveScroll>
  );
}

function ModalFooter(props: { children: ReactNode }) {
  return (
    <Box
      padding={["3", "5"]}
      style={{ boxShadow: "0px -4px 8px rgba(0, 0, 0, 0.1)" }}
    >
      {props.children}
      <SafeAreaBottom />
    </Box>
  );
}
